import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { api, formatPrice } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { usePromo, applyDiscount } from "../components/PromoBanner";

const emptyForm = {
  name: "",
  email: "",
  mobile: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
};

const Checkout = () => {
  const { items, subtotalFormatted, subtotal, clear } = useCart();
  const promo = usePromo();
  const hasSale = promo?.active && promo.discount_pct > 0;
  const isBogo = promo?.active && promo.type === "bogo";
  const discountAmt = hasSale ? Math.round(subtotal * promo.discount_pct / 100) : 0;
  const finalAmt = subtotal - discountAmt;
  const finalFormatted = finalAmt.toLocaleString("en-IN");
  const nav = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (items.length === 0) {
      // if user reloaded with empty cart, bounce to shop
      const t = setTimeout(() => nav("/shop", { replace: true }), 200);
      return () => clearTimeout(t);
    }
  }, [items.length, nav]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Valid email required";
    const mob = form.mobile.replace(/\D/g, "");
    if (mob.length < 10) e.mobile = "Enter a valid mobile";
    if (!form.address_line1.trim()) e.address_line1 = "Address is required";
    if (!form.city.trim()) e.city = "City required";
    if (!form.state.trim()) e.state = "State required";
    if (!/^\d{6}$/.test(form.pincode.replace(/\D/g, ""))) e.pincode = "6-digit PIN";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const placeOrder = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    if (!window.Razorpay) {
      toast.error("Payment SDK not loaded", { description: "Refresh and try again." });
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/orders/create-order", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        customer: form,
      });

      const opts = {
        key: r.data.key_id,
        amount: r.data.amount,
        currency: r.data.currency,
        order_id: r.data.razorpay_order_id,
        name: r.data.name,
        description: r.data.description,
        prefill: r.data.prefill,
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify", {
              order_id: r.data.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            nav(`/checkout/success?order_id=${r.data.order_id}`, { replace: true });
          } catch (err) {
            setSubmitting(false);
            toast.error("Verification failed", {
              description:
                err?.response?.data?.detail ||
                "Please contact support with your payment ID.",
            });
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast("Payment cancelled", { description: "You closed the checkout window." });
          },
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (err) => {
        setSubmitting(false);
        toast.error("Payment failed", {
          description: err?.error?.description || "Please try again.",
        });
      });
      rzp.open();
    } catch (err) {
      setSubmitting(false);
      toast.error("Couldn't start payment", {
        description: err?.response?.data?.detail || "Please try again in a moment.",
      });
    }
  };

  const inputCls = (k) =>
    `w-full bg-transparent border ${
      errors[k] ? "border-[#E36B6B]" : "border-[#2D2622]"
    } focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-[#F4EAD9] placeholder:text-[#5C4F4A] transition-colors`;

  return (
    <div data-testid="checkout-page" className="min-h-screen">
      <Header />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-24">
        <p className="text-[11px] tracking-overline uppercase font-bold text-[#D4AF37] mb-6">
          Checkout · Secure payment via Razorpay
        </p>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl text-[#F4EAD9] leading-[0.95] mb-16">
          One <span className="italic text-[#D4AF37]">last</span> step.
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Address form */}
          <form
            data-testid="checkout-form"
            onSubmit={placeOrder}
            className="lg:col-span-7 space-y-6"
          >
            <div>
              <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496] mb-4">
                Contact
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input
                    data-testid="ck-name"
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className={inputCls("name")}
                  />
                  {errors.name && <p className="mt-1 text-xs text-[#E36B6B]">{errors.name}</p>}
                </div>
                <div>
                  <input
                    data-testid="ck-mobile"
                    placeholder="Mobile number"
                    value={form.mobile}
                    onChange={(e) => set("mobile", e.target.value)}
                    className={inputCls("mobile")}
                  />
                  {errors.mobile && <p className="mt-1 text-xs text-[#E36B6B]">{errors.mobile}</p>}
                </div>
              </div>
              <div className="mt-4">
                <input
                  data-testid="ck-email"
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={inputCls("email")}
                />
                {errors.email && <p className="mt-1 text-xs text-[#E36B6B]">{errors.email}</p>}
              </div>
            </div>

            <div>
              <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496] mb-4">
                Shipping address
              </p>
              <div className="space-y-4">
                <div>
                  <input
                    data-testid="ck-addr1"
                    placeholder="Address line 1 (house, street)"
                    value={form.address_line1}
                    onChange={(e) => set("address_line1", e.target.value)}
                    className={inputCls("address_line1")}
                  />
                  {errors.address_line1 && (
                    <p className="mt-1 text-xs text-[#E36B6B]">{errors.address_line1}</p>
                  )}
                </div>
                <input
                  data-testid="ck-addr2"
                  placeholder="Address line 2 (landmark, apt) — optional"
                  value={form.address_line2}
                  onChange={(e) => set("address_line2", e.target.value)}
                  className={inputCls("address_line2")}
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <input
                      data-testid="ck-city"
                      placeholder="City"
                      value={form.city}
                      onChange={(e) => set("city", e.target.value)}
                      className={inputCls("city")}
                    />
                    {errors.city && <p className="mt-1 text-xs text-[#E36B6B]">{errors.city}</p>}
                  </div>
                  <div>
                    <input
                      data-testid="ck-state"
                      placeholder="State"
                      value={form.state}
                      onChange={(e) => set("state", e.target.value)}
                      className={inputCls("state")}
                    />
                    {errors.state && <p className="mt-1 text-xs text-[#E36B6B]">{errors.state}</p>}
                  </div>
                  <div>
                    <input
                      data-testid="ck-pincode"
                      placeholder="PIN"
                      value={form.pincode}
                      onChange={(e) => set("pincode", e.target.value)}
                      className={inputCls("pincode")}
                    />
                    {errors.pincode && (
                      <p className="mt-1 text-xs text-[#E36B6B]">{errors.pincode}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-[#2D2622]">
              <button
                data-testid="ck-place-order"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#B8941F] disabled:opacity-70 disabled:cursor-not-allowed text-[#15110F] px-10 py-4 text-[11px] tracking-overline uppercase font-bold transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} strokeWidth={1.2} className="animate-spin" />
                    Opening payment
                  </>
                ) : (
                  <>Place order — ₹{finalFormatted}</>
                )}
              </button>
              <div className="mt-4 flex items-center gap-2 text-xs text-[#B0A496]">
                <ShieldCheck size={14} strokeWidth={1.2} className="text-[#D4AF37]" />
                Payments secured by Razorpay · UPI / Cards / NetBanking / Wallets
              </div>
            </div>
          </form>

          {/* Order summary */}
          <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
            <div className="bg-[#0F0C0A] border border-[#2D2622] p-6">
              <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496] mb-5">
                Your order
              </p>
              <ul className="divide-y divide-[#2D2622]">
                {items.map((it) => (
                  <li
                    key={it.product_id}
                    data-testid={`sum-${it.product_id}`}
                    className="flex gap-4 py-4"
                  >
                    <div className="w-16 aspect-square overflow-hidden bg-[#1F1A17]">
                      <img
                        src={it.image_url}
                        alt={it.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-serif-display text-lg text-[#F4EAD9]">{it.name}</p>
                      <p className="text-xs text-[#B0A496] mt-1">
                        Qty {it.qty}
                        {isBogo && (
                          <span data-testid={`sum-bogo-${it.product_id}`} className="text-[#D4AF37] font-bold"> + {it.qty} FREE</span>
                        )}
                      </p>
                    </div>
                    <p className="text-sm tabular-nums text-[#D4AF37]">
                      {formatPrice(it.price_inr)}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="border-t border-[#2D2622] mt-4 pt-4 space-y-2">
                <div className="flex justify-between items-baseline">
                  <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496]">Subtotal</p>
                  <p className="text-sm tabular-nums text-[#F4EAD9]">₹{subtotalFormatted}</p>
                </div>
                {hasSale && (
                  <div data-testid="checkout-discount-row" className="flex justify-between items-baseline">
                    <p className="text-[11px] tracking-overline uppercase font-bold text-[#D4AF37]">
                      {promo.label} · {promo.discount_pct}% off
                    </p>
                    <p className="text-sm tabular-nums text-[#D4AF37]">
                      − ₹{discountAmt.toLocaleString("en-IN")}
                    </p>
                  </div>
                )}
                {isBogo && (
                  <div data-testid="checkout-bogo-row" className="flex justify-between items-baseline">
                    <p className="text-[11px] tracking-overline uppercase font-bold text-[#D4AF37]">
                      {promo.label} · Buy 1 Get 1 Free
                    </p>
                    <p className="text-sm tabular-nums text-[#D4AF37]">
                      +{items.reduce((s, i) => s + i.qty, 0)} free
                    </p>
                  </div>
                )}
                <div className="flex justify-between items-baseline">
                  <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496]">Shipping</p>
                  <p className="text-sm tabular-nums text-[#F4EAD9]">Free · PAN India</p>
                </div>
                <div className="pt-3 border-t border-[#2D2622] flex justify-between items-baseline">
                  <p className="text-[11px] tracking-overline uppercase font-bold text-[#B0A496]">Total</p>
                  <p className="text-2xl tabular-nums text-[#D4AF37] font-medium">
                    ₹{finalFormatted}
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Checkout;
